import { Heading, Link, Text, View } from "@instructure/ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { clearCognitoSession } from "../utils/amplifyConfig";

const Signout = () => {
  const { t } = useTranslation();
  // Whether the person signed out of the Cognito login path (vs Canvas/LTI).
  // Drives the post-sign-out messaging: Cognito users get a "log in again" link
  // to `/login`; LTI users keep the "close this tab / return to Canvas" message
  // since they have no Insights login page to return to.
  const [wasCognito, setWasCognito] = useState(false);

  // The backend `/signout` (which redirects here) clears the `ddl`/`ddl_o_t`
  // cookies, but the Cognito login path also leaves an Amplify session in
  // `localStorage`. Clear it here so signing out sticks: otherwise the next
  // visit to `/login` sees a still-authenticated Cognito session and silently
  // re-mints a `ddl` cookie. `clearCognitoSession` also reports whether a
  // Cognito session existed, which we use to tailor the message below. Best-
  // effort and a no-op for Canvas/LTI users who have no Cognito session.
  useEffect(() => {
    let cancelled = false;
    clearCognitoSession().then((hadCognitoSession) => {
      if (!cancelled) {
        setWasCognito(hadCognitoSession);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    // Plain div for the centering container: Instructure's <View> does not
    // reliably forward an arbitrary inline `style` object to the DOM, so the
    // flex/centering must live on a real element to actually apply.
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        minHeight: "80vh",
        width: "100%",
        padding: "1.5rem",
      }}
    >
      <Heading as="h1" margin="0 0 medium 0">
        {t("signout.heading")}
      </Heading>
      <Text size="medium">
        {wasCognito ? t("signout.cognitoMessage") : t("signout.ltiMessage")}
      </Text>
      {wasCognito && (
        <View as="div" margin="medium 0 0 0">
          <Link href="/login">{t("signout.loginAgain")}</Link>
        </View>
      )}
    </div>
  );
};

export default Signout;
